import React, { useState } from "react";
import {
  MapPin,
  Share2,
  ArrowRight,
  Building2,
  Briefcase,
  ShieldCheck,
  Sparkles,
  PhoneCall,
  Search,
  Clock,
  CheckCircle2,
  Layers,
} from "lucide-react";
import { motion } from "framer-motion";

const leaseProperties = [
  {
    id: "itpl-tech-corridor",
    title: "ITPL Tech Park Plug & Play Office",
    location: "ITPL Main Road, Whitefield, Bengaluru",
    price: "₹2,20,000",
    period: "/ month",
    category: "Commercial Office",
    lockIn: "3 Years Lock-in",
    area: "3,500 ft²",
    capacity: "45 Workstations",
    fitout: "Fully Furnished",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=85",
    description:
      "Grade-A plug-and-play corporate tech workspace with reception, 2 conference rooms, 4 manager cabins, high-speed fiber, and 100% DG backup.",
  },
  {
    id: "outer-ring-road-villa",
    title: "Bellandur Corporate Residential Villa",
    location: "Outer Ring Road, Bellandur, Bengaluru",
    price: "₹1,60,000",
    period: "/ month",
    category: "Corporate Living",
    lockIn: "2 Years Minimum",
    area: "4,200 ft²",
    capacity: "4 BHK Luxury Villa",
    fitout: "Designer Furnished",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=85",
    description:
      "Exclusive gated community villa ideal for senior MNC executives and expatriates, featuring private garden, Italian marble, and clubhouse access.",
  },
  {
    id: "indiranagar-commercial",
    title: "Indiranagar 100ft Road Prime Retail",
    location: "100ft Road, Indiranagar, Bengaluru",
    price: "₹2,80,000",
    period: "/ month",
    category: "Commercial Retail",
    lockIn: "5 Years Long-term",
    area: "2,800 ft²",
    capacity: "Ground + 1st Floor",
    fitout: "Warm Shell",
    image:
      "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1200&q=85",
    description:
      "High-street commercial retail showroom with massive 40ft glass frontage, high footfall, 3 dedicated basement car parks, and 3-phase commercial power.",
  },
  {
    id: "epip-zone-whitefield",
    title: "EPIP Zone Grade-A Enterprise Floor",
    location: "EPIP Industrial Zone, Whitefield, Bengaluru",
    price: "₹3,50,000",
    period: "/ month",
    category: "Commercial Office",
    lockIn: "3 - 5 Years",
    area: "5,200 ft²",
    capacity: "70 Workstations",
    fitout: "Plug & Play",
    image:
      "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=1200&q=85",
    description:
      "Independent floor inside a prime tech campus near metro station, featuring server room, biometric access, cafeteria, and EV charging bays.",
  },
  {
    id: "soukya-road-long-lease",
    title: "Soukya Road Long-Term Luxury Rowhouse",
    location: "Soukya Road, Whitefield, Bengaluru",
    price: "₹75,000",
    period: "/ month",
    category: "Corporate Living",
    lockIn: "3 Years Lock-in",
    area: "2,600 ft²",
    capacity: "3 Bed + Study",
    fitout: "Semi-Furnished",
    image:
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=85",
    description:
      "Long-term residential lease with fixed rental escalation, clubhouse amenities, private terrace, and 10-minute commute to Whitefield IT hub.",
  },
  {
    id: "embassy-tech-village-penthouse",
    title: "Devarabisanahalli Executive Penthouse",
    location: "Near Embassy Tech Village, ORR, Bengaluru",
    price: "₹1,40,000",
    period: "/ month",
    category: "Corporate Living",
    lockIn: "2 Years Minimum",
    area: "3,100 ft²",
    capacity: "4 BHK Penthouse",
    fitout: "Fully Furnished",
    image:
      "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1200&q=85",
    description:
      "High-floor duplex penthouse overlooking ORR skyline, with expansive private party deck, smart automated lights, and imported German appliances.",
  },
];

const leaseCategories = [
  "All",
  "Commercial Office",
  "Corporate Living",
  "Commercial Retail",
];

const Lease = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredProperties = leaseProperties.filter((property) => {
    const matchesCategory =
      selectedCategory === "All" || property.category === selectedCategory;
    const matchesSearch =
      property.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.fitout.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleShare = (property, e) => {
    e.preventDefault();
    e.stopPropagation();

    const message = `🏢 *${property.title}*

📍 Location: ${property.location}
💼 Category: ${property.category}
💰 Lease Rate: ${property.price} ${property.period}
📐 Area: ${property.area}
👥 Capacity/Type: ${property.capacity}
🔒 Term: ${property.lockIn}
🛋 Fitout: ${property.fitout}

${property.description}

Hi HousyHunt, I'm interested in leasing this property. Please share the lease agreement terms and arrange a site inspection.`;

    const whatsappURL = `https://wa.me/918088687429?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappURL, "_blank");
  };

  return (
    <div className="w-full bg-[#FAFCFF] min-h-screen">
      {/* ================= HERO HEADER ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-red-50/80 via-white to-[#FAFCFF] pt-12 pb-16 px-4 sm:px-6 lg:px-14 border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 bg-red-100/70 text-red-600 border border-red-200/80 px-4 py-2 rounded-full text-xs sm:text-sm font-bold tracking-wide uppercase mb-4 shadow-sm">
              <Sparkles size={15} />
              Commercial & Corporate Lease in Bengaluru
            </span>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 tracking-tight leading-[1.15] max-w-4xl">
              Prime Spaces for Lease,{" "}
              <span className="text-red-600">
                Tailored for Businesses & Executives.
              </span>
            </h1>

            <p className="mt-5 text-gray-600 text-base sm:text-lg max-w-2xl leading-relaxed">
              Explore verified plug-and-play offices, high-street retail spaces,
              and long-term corporate residential enclaves across Bengaluru’s most
              connected business corridors.
            </p>

            {/* SEARCH */}
            <div className="mt-8 w-full max-w-2xl">
              <div className="relative w-full">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by location (e.g. ITPL, Indiranagar), space type, or capacity..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition shadow-sm text-sm"
                />
              </div>
            </div>

            {/* CATEGORY PILLS */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {leaseCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    selectedCategory === cat
                      ? "bg-red-600 text-white shadow-md shadow-red-500/20"
                      : "bg-white text-gray-700 border border-gray-200 hover:border-red-300 hover:text-red-600"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= PROPERTY CARDS GRID ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-14 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-gray-900">
              Verified Properties for Lease
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Showing {filteredProperties.length} active lease listings in Bengaluru
            </p>
          </div>

          <a
            href="https://wa.me/message/MDG2ZSZNG6P6G1"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-red-600 hover:text-red-700"
          >
            <PhoneCall size={16} /> Custom Commercial Lease Requirement? Chat with us
          </a>
        </div>

        {filteredProperties.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
            <Building2 size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-bold text-gray-800">
              No lease spaces match your criteria
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              Try adjusting your search keywords or category filters.
            </p>
            <button
              onClick={() => {
                setSelectedCategory("All");
                setSearchQuery("");
              }}
              className="mt-5 px-6 py-2.5 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProperties.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group"
              >
                {/* IMAGE CONTAINER */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />

                  {/* GRADIENT OVERLAY */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

                  {/* TOP BADGES */}
                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                    <span className="bg-red-600/90 backdrop-blur-md text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow">
                      {item.category}
                    </span>

                    <button
                      type="button"
                      onClick={(e) => handleShare(item, e)}
                      aria-label={`Share ${item.title}`}
                      className="w-9 h-9 rounded-full bg-white/90 backdrop-blur-md hover:bg-white text-gray-700 hover:text-black flex items-center justify-center shadow transition-all duration-200 hover:scale-105"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>

                  {/* BOTTOM RENT BADGE */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                    <div>
                      <span className="text-[11px] uppercase tracking-wider text-red-200 font-semibold">
                        Lease Rate
                      </span>
                      <p className="text-2xl font-black drop-shadow-sm">
                        {item.price}{" "}
                        <span className="text-xs font-normal text-white/80">
                          {item.period}
                        </span>
                      </p>
                    </div>

                    <span className="text-xs font-semibold bg-white/20 backdrop-blur-md text-white px-3 py-1 rounded-full border border-white/30">
                      {item.lockIn}
                    </span>
                  </div>
                </div>

                {/* CONTENT */}
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>

                  <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-2">
                    <MapPin size={14} className="text-red-500 shrink-0" />
                    <span className="truncate">{item.location}</span>
                  </div>

                  <p className="text-gray-600 text-xs mt-3 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>

                  {/* SPECS GRID */}
                  <div className="grid grid-cols-3 gap-2 mt-5 p-3 rounded-2xl bg-gray-50 border border-gray-100 text-xs text-center text-gray-700">
                    <div>
                      <div className="flex items-center justify-center gap-1 text-red-600 mb-0.5">
                        <Layers size={14} />
                      </div>
                      <span className="font-bold">{item.area}</span>
                    </div>

                    <div>
                      <div className="flex items-center justify-center gap-1 text-red-600 mb-0.5">
                        <Briefcase size={14} />
                      </div>
                      <span className="font-bold truncate">{item.capacity}</span>
                    </div>

                    <div>
                      <div className="flex items-center justify-center gap-1 text-red-600 mb-0.5">
                        <Clock size={14} />
                      </div>
                      <span className="font-bold truncate">{item.fitout}</span>
                    </div>
                  </div>

                  {/* ACTION BUTTONS */}
                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={(e) => handleShare(item, e)}
                      className="flex-1 py-3 px-4 bg-gray-900 hover:bg-red-600 text-white rounded-xl font-semibold text-xs transition-colors duration-300 flex items-center justify-center gap-1.5 group/btn"
                    >
                      <span>Enquire Lease</span>
                      <ArrowRight
                        size={14}
                        className="group-hover/btn:translate-x-1 transition-transform"
                      />
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(
                          "https://wa.me/message/MDG2ZSZNG6P6G1",
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }}
                      className="py-3 px-5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-xs transition-colors shadow-md hover:shadow-red-500/20"
                    >
                      Contact
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* ================= LEASE BENEFITS ================= */}
      <section className="bg-white py-16 px-4 sm:px-6 lg:px-14 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100 flex items-start gap-4">
              <div className="p-3 bg-red-100 text-red-600 rounded-2xl shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Commercial Due Diligence
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Clear title deeds, commercial zoning verification, and OC/fire
                  compliance documentation.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100 flex items-start gap-4">
              <div className="p-3 bg-red-100 text-red-600 rounded-2xl shrink-0">
                <ShieldCheck size={24} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Flexible Lease Structures
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Tailored lock-in, escalation clauses, and fit-out rent-free
                  periods negotiated on your behalf.
                </p>
              </div>
            </div>

            <div className="p-6 rounded-3xl bg-gray-50 border border-gray-100 flex items-start gap-4">
              <div className="p-3 bg-red-100 text-red-600 rounded-2xl shrink-0">
                <Sparkles size={24} />
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-base">
                  Corporate Concierge
                </h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  End-to-end legal drafting, registration, stamp duty, and utility
                  handover coordination.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Lease;
